import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { NetworkStatusService, NetworkConnectionState } from '../../services/network-status.service';

@Component({
  selector: 'app-network-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (status !== 'online') {
      <aside
        class="network-banner"
        [class.is-offline]="status === 'offline'"
        [class.is-reconnecting]="status === 'reconnecting'"
        [class.is-restored]="status === 'restored'"
        role="alert"
        aria-live="assertive"
      >
        <div class="banner-inner">
          <div class="banner-leading">
            @if (status === 'offline') {
              <!-- Warning Triangle Icon -->
              <svg class="banner-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            } @else if (status === 'reconnecting') {
              <!-- Spinner Icon -->
              <svg class="banner-icon spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
                <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
              </svg>
            } @else if (status === 'restored') {
              <!-- Checkmark Circle Icon -->
              <svg class="banner-icon check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            }

            <span class="banner-message">
              @if (status === 'offline') {
                Something went wrong while connecting to WyberAI. Reconnect to keep working smoothly.
              } @else if (status === 'reconnecting') {
                Reconnecting to WyberAI services...
              } @else if (status === 'restored') {
                Connection restored. You're back online.
              }
            </span>
          </div>

          <div class="banner-actions">
            @if (status === 'offline') {
              <button
                type="button"
                class="btn-reconnect"
                (click)="onReconnectClick()"
                aria-label="Reconnect to WyberAI"
              >
                Reconnect now
              </button>
            } @else if (status === 'reconnecting') {
              <span class="reconnecting-hint">Checking connection...</span>
            }
          </div>
        </div>
      </aside>
    }
  `,
  styles: [`
    :host {
      display: block;
    }

    .network-banner {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 38px;
      box-sizing: border-box;
      z-index: 99998; /* Just beneath modals/dialogs, but above all page content */
      padding: 0 16px;
      display: flex;
      align-items: center;
      font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 13.5px;
      line-height: 1.4;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      animation: slideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      transition: background-color 0.3s ease, border-color 0.3s ease;
    }

    @keyframes slideDown {
      from {
        transform: translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    /* Teams-style dark amber / brown alert theme */
    .network-banner.is-offline {
      background: #78350f;
      border-bottom: 1px solid #b45309;
      color: #fef3c7;
    }

    /* Reconnecting theme - deeper amber with glowing pulse */
    .network-banner.is-reconnecting {
      background: #854d0e;
      border-bottom: 1px solid #ca8a04;
      color: #fef9c3;
    }

    /* Restored theme - clean forest green */
    .network-banner.is-restored {
      background: #14532d;
      border-bottom: 1px solid #16a34a;
      color: #dcfce7;
      animation: slideDown 0.3s ease-out;
    }

    .banner-inner {
      max-width: 1380px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    .banner-leading {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 260px;
    }

    .banner-icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      stroke-width: 2.2;
    }

    .banner-icon.spinner {
      animation: spin 0.85s linear infinite;
    }

    .banner-icon.check {
      color: #86efac;
    }

    @keyframes spin {
      100% {
        transform: rotate(360deg);
      }
    }

    .banner-message {
      font-weight: 500;
      letter-spacing: -0.01em;
    }

    .banner-actions {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .btn-reconnect {
      background: rgba(255, 255, 255, 0.18);
      color: #ffffff;
      border: 1px solid rgba(255, 255, 255, 0.35);
      border-radius: 6px;
      padding: 4px 12px;
      font-size: 12.5px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.15s ease-in-out;
    }

    .btn-reconnect:hover {
      background: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.6);
      transform: translateY(-1px);
    }

    .btn-reconnect:active {
      transform: translateY(0);
    }

    .reconnecting-hint {
      font-size: 12px;
      opacity: 0.85;
      font-style: italic;
    }

    @media (max-width: 640px) {
      .banner-inner {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }
      .banner-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }
  `]
})
export class NetworkBannerComponent implements OnInit, OnDestroy {
  private readonly networkService = inject(NetworkStatusService);
  private readonly cdr = inject(ChangeDetectorRef);
  private subscription?: Subscription;

  status: NetworkConnectionState = 'online';

  ngOnInit(): void {
    this.status = this.networkService.currentStatus;
    this.updateBodyClass(this.status);
    this.subscription = this.networkService.status$.subscribe(state => {
      this.status = state;
      this.updateBodyClass(state);
      this.cdr.detectChanges();
    });
  }

  private updateBodyClass(state: NetworkConnectionState): void {
    if (typeof document === 'undefined') return;
    if (state !== 'online') {
      document.body.classList.add('has-network-banner');
      document.documentElement.style.setProperty('--network-banner-height', '38px');
    } else {
      document.body.classList.remove('has-network-banner');
      document.documentElement.style.setProperty('--network-banner-height', '0px');
    }
  }

  async onReconnectClick(): Promise<void> {
    const isUp = await this.networkService.retryConnection();
    if (isUp && typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (typeof document !== 'undefined') {
      document.body.classList.remove('has-network-banner');
      document.documentElement.style.setProperty('--network-banner-height', '0px');
    }
  }
}
